// ===================================================================
// KAIXO - Live subtitles helper
// ===================================================================
// Responsable de:
//  1) Capturar el audio local del usuario (con getUserMedia dedicado).
//  2) Remuestrear a 16kHz mono 16-bit PCM (lo que espera Deepgram).
//  3) Enviar chunks por socket.io al backend en binario.
//
// El reconocimiento y la traducción suceden en el backend; el texto
// traducido llega al CallManager vía LiveKit DataChannel — este módulo
// NO maneja la recepción, solo el envío del audio del hablante.
// ===================================================================

// Sample rate destino (Deepgram linear16 default = 16000)
const TARGET_SR = 16000;

/**
 * Crea una captura de audio lista para enviar al backend.
 * Devuelve un objeto con stop() para cerrar limpiamente.
 *
 * @param {object} opts
 * @param {MediaStream} [opts.stream]  - opcional; si no hay track vivo, se
 *                                       pide uno nuevo con getUserMedia
 * @param {Function} opts.onChunk      - callback(ArrayBuffer PCM 16-bit)
 */
export async function startAudioCapture({ stream, onChunk }) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) throw new Error('AudioContext no disponible');

    // Algunos navegadores no dejan que un mismo MediaStreamTrack se lea
    // desde dos consumidores en paralelo (LiveKit ya lo está usando).
    // Pedimos uno dedicado si el pasado no tiene audio activo.
    let workingStream = stream;
    const hasLiveAudio = stream
        && stream.getAudioTracks
        && stream.getAudioTracks().some(t => t.readyState === 'live' && !t.muted);
    if (!hasLiveAudio) {
        try {
            // Mismas constraints que LiveKit para evitar doble eco y que
            // la transcripción reciba audio limpio.
            workingStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
        } catch (err) {
            throw new Error('No se pudo acceder al micrófono para subtítulos: ' + err.message);
        }
    }

    const ctx = new AudioCtx();
    // Política de autoplay: en móvil el AudioContext arranca 'suspended'.
    // Hay que reanudar o no dispara onaudioprocess (fallo silencioso
    // muy difícil de diagnosticar).
    if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch {}
    }
    const sourceSR = ctx.sampleRate;  // típicamente 44100 o 48000
    const source = ctx.createMediaStreamSource(workingStream);

    // ScriptProcessorNode está deprecated pero es universalmente
    // compatible (AudioWorklet no funciona en todos los Android WebView).
    // Buffer 2048 a 48kHz ≈ 42ms por chunk — menos latencia que 4096,
    // a cambio de más overhead (CPU+red). Para voz va perfecto.
    const bufSize = 2048;
    const processor = ctx.createScriptProcessor(bufSize, 1, 1);

    const ratio = sourceSR / TARGET_SR;
    let chunkCount = 0;

    processor.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);
        // Downsample por decimación con promedio del bloque — adecuado
        // para voz. No es Nyquist estricto pero Deepgram lo tolera.
        const outLen = Math.floor(input.length / ratio);
        const out = new Int16Array(outLen);
        let j = 0;
        for (let i = 0; i < outLen; i++) {
            const start = Math.floor(i * ratio);
            const end = Math.min(input.length, Math.floor((i + 1) * ratio));
            let sum = 0;
            for (let k = start; k < end; k++) sum += input[k];
            const avg = sum / Math.max(1, end - start);
            const s = Math.max(-1, Math.min(1, avg));
            out[j++] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        chunkCount++;
        if (chunkCount === 1) {
            console.log(`[subtitles] primer chunk emitido ${out.buffer.byteLength} bytes (sr: ${sourceSR}→16000)`);
        } else if (chunkCount % 100 === 0) {
            console.log(`[subtitles] ${chunkCount} chunks emitidos`);
        }
        try { onChunk(out.buffer); } catch {}
    };

    source.connect(processor);
    // Conectar a destino con gain=0 para que onaudioprocess dispare sin
    // que el usuario oiga su propio eco.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    processor.connect(mute);
    mute.connect(ctx.destination);

    let closed = false;
    return {
        stop() {
            if (closed) return;
            closed = true;
            try { processor.disconnect(); } catch {}
            try { source.disconnect(); } catch {}
            try { mute.disconnect(); } catch {}
            try { ctx.close(); } catch {}
            // Si pedimos getUserMedia propio, liberarlo para que no quede
            // el micrófono "en uso" tras cerrar la llamada.
            if (workingStream !== stream) {
                try { workingStream.getTracks().forEach(t => t.stop()); } catch {}
            }
        }
    };
}
